<template>
  <div
    class="disputed-calls-card"
    data-cy="disputed-calls-card"
  >
    <div class="d-flex align-items-center justify-content-between disputed-calls-card__header">
      <h6 class="disputed-calls-card__title">
        <font-awesome-icon
          :icon="['fas', 'exclamation-triangle']"
          class="disputed-calls-card__icon"
        />
        Disputes
        <span
          v-if="disputedCalls.length > 0"
          class="disputed-calls-card__count"
          data-cy="disputed-calls-card-count"
        >({{ disputedCalls.length }})</span>
      </h6>

      <button
        v-if="disputedCalls.length > 0"
        type="button"
        class="btn btn-link btn-sm p-0 text-muted"
        data-cy="disputed-calls-card-view-all"
        @click="$emit('view-all')"
      >
        View all
      </button>
    </div>

    <div
      v-if="disputedCalls.length === 0"
      class="disputed-calls-card__empty"
      data-cy="disputed-calls-card-empty"
    >
      No disputed calls in the selected period
    </div>

    <ul
      v-else
      class="disputed-calls-card__list"
    >
      <li
        class="disputed-calls-card__row disputed-calls-card__row--head"
        aria-hidden="true"
        data-cy="disputed-calls-card-head"
      >
        <span class="disputed-calls-card__date">Resolved</span>
        <span class="disputed-calls-card__caller">Caller ID</span>
        <span class="disputed-calls-card__amount">Adjustment</span>
      </li>
      <li
        v-for="call in latestCalls"
        :key="call.callId"
        class="disputed-calls-card__row"
        data-cy="disputed-calls-card-row"
      >
        <span class="disputed-calls-card__date">
          {{ call.disputeResolvedTimestamp ? dateFormat(call.disputeResolvedTimestamp, 'MMM DD hh:mm A') : '-' }}
        </span>
        <span class="disputed-calls-card__caller">{{ call.callerId || '-' }}</span>
        <span
          class="disputed-calls-card__amount"
          :class="call.disputedAmount < 0 ? 'adjustment--lowered' : 'adjustment--raised'"
        >
          {{ formatCurrency(call.disputedAmount) }}
        </span>
      </li>
    </ul>
  </div>
</template>

<script>
import dateFormatMixin from '@/mixins/dateFormatMixin'
import formatCurrency from '@/utils/formatCurrency'

const PREVIEW_ROWS = 3

export default {
  name: 'DisputedCallsCard',

  mixins: [dateFormatMixin],

  props: {
    // The buyer's disputed calls in the window, as /disputed-calls returns them
    disputedCalls: {
      type: Array,
      default: () => []
    }
  },

  computed: {
    // Most recently resolved first, like the modal. /disputed-calls only returns settled disputes (the Billing DB records
    // a dispute when an admin resolves it), so every row has a resolution date
    latestCalls() {
      const resolvedAt = call => call.disputeResolvedTimestamp ? new Date(call.disputeResolvedTimestamp).getTime() : 0

      return [...this.disputedCalls]
        .sort((a, b) => resolvedAt(b) - resolvedAt(a))
        .slice(0, PREVIEW_ROWS)
    }
  },

  methods: {
    formatCurrency
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/components/call-logs-colors';

.adjustment--lowered {
  color: $call-logs-green;
}

.adjustment--raised {
  color: $call-logs-red;
}

.disputed-calls-card__header {
  margin-bottom: 0.5rem;
}

.disputed-calls-card__title {
  font-weight: bold;
  margin-bottom: 0;
}

.disputed-calls-card__icon {
  color: $call-logs-red;
  margin-right: 0.25rem;
}

.disputed-calls-card__count {
  font-weight: normal;
  opacity: 0.6;
  margin-left: 0.25rem;
}

.disputed-calls-card__empty {
  font-size: 0.8rem;
  opacity: 0.7;
}

.disputed-calls-card__list {
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 0.8rem;
  // Scrolls sideways when the card is narrower than a row, instead of letting it spill out; thin and dim on the dark theme
  overflow-x: auto;
  scrollbar-color: rgba(138, 148, 166, 0.5) transparent;
  scrollbar-width: thin;
}

.disputed-calls-card__row {
  display: flex;
  gap: 0.75rem;
  min-width: max-content;
  padding: 0.2rem 0;
  white-space: nowrap;
}

.disputed-calls-card__date {
  min-width: 7.5rem;
}

.disputed-calls-card__caller {
  flex: 1 0 auto;
}

.disputed-calls-card__amount {
  text-align: right;
}

// Column names, bold like the Performance card's table headers
.disputed-calls-card__row--head {
  font-weight: bold;
}
</style>
